/* eslint-disable max-len */
import React from "react"
import { GatsbyImage, getImage } from "gatsby-plugin-image"

import CroppedImagePreload from "components/cropped-image-preload"

import {
  Wrapper,
  ImageCropped,
  Media,
  CreditSource,
  Disclaimer,
  DisclaimerLink,
  DateWrapper,
} from "./styles"

const Header = ({ article, element, children }) => {
  const {
    children: imagesGatsby,
    showAffiliateDisclaimer,
    publishDate,
    publishDateFormatted,
    lastModifiedFormatted,
    template,
    indexForSearch,
    partner,
    sponsor,
    titles: { body: bodyTitle, site: siteTitle },
  } = article

  const isFeatured = template === "feature"

  const { image } = element
  const imageGatsby = imagesGatsby?.find(({ type }) => type === "header")
  const showImageUrlList = [
    "collagen-powders",
    "best-collagen-supplements",
    "cariuma-oca-low-sneaker-review",
    "what-is-considered-cheating-in-relationship",
    "best-probiotics",
    "frozen-meal-delivery",
    "meal-delivery-for-weight-loss",
    "best-plant-based-meal-delivery-service",
    "best-liver-supplements-for-healthy-detox",
  ]
  const onUrlList = showImageUrlList.some((url) => new RegExp(`\/?${url}`).test(article.url))

  const hasPartnerOrSponsor = Boolean(partner || sponsor)
  const publishedMoreThan30DaysAgo =
    new Date().getTime() - 30 * 24 * 60 * 60 * 1000 > new Date(publishDate)
  // check if the image should be credited to mbg creative, in which case it should always show
  const mbgCreative = /mbg creative/i.test(image?.source) || /mbg creative/i.test(image?.credit)

  const hideImage =
    !mbgCreative &&
    !onUrlList &&
    publishedMoreThan30DaysAgo &&
    indexForSearch &&
    !hasPartnerOrSponsor

  const headerSizes = {
    1024: isFeatured ? [1440, 810] : [900, 500],
    768: isFeatured ? [1023, 575] : [1023, 575],
    0: isFeatured ? [480, 322] : [480, 322],
  }

  const sizes = image
    ? [
        {
          query: 1024,
          width: headerSizes[1024][0],
          height: headerSizes[1024][1],
          transforms: `c_fill,g_auto,w_${headerSizes[1024][0]},h_${headerSizes[1024][1]},q_auto,f_auto,fl_lossy`,
        },
        {
          query: 768,
          width: headerSizes[768][0],
          height: headerSizes[768][1],
          transforms: `c_fill,g_auto,w_${headerSizes[768][0]},h_${headerSizes[768][1]},q_auto,f_auto,fl_lossy`,
        },
        {
          width: headerSizes[0][0],
          height: headerSizes[0][1],
          transforms: `c_fill,g_auto,w_${headerSizes[0][0]},h_${headerSizes[0][1]},q_auto,f_auto,fl_lossy`,
        },
      ]
    : []

  const formatCreditSource = ({ credit, source, imageType }) => {
    const by = imageType ? `${imageType} by` : "Image by"

    if (credit && !mbgCreative) {
      return `${by} <b>${credit}</b>${source ? ` / ${source}` : ""}`
    }
    if (credit && mbgCreative) {
      return `${by} ${credit}${source ? ` / ${source}` : ""}`
    }

    if (source) {
      return `${by} ${source}`
    }

    return ""
  }

  const displayDate = lastModifiedFormatted || publishDateFormatted

  return (
    <Wrapper hideImage={hideImage} image={image} isFeatured={isFeatured}>
      {children}
      {!hideImage && image && (
        <>
          {imageGatsby ? (
            <GatsbyImage
              id="article-header-image"
              role="presentation"
              image={getImage(imageGatsby.localImage)}
              alt={image.altText || image.title || bodyTitle || siteTitle}
              title={image.altText || image.title || bodyTitle || siteTitle}
              loading="eager"
              placeholder="none"
              style={{ ...(isFeatured && { width: "100%" }), marginBottom: "2rem" }}
            />
          ) : (
            <>
              <CroppedImagePreload imageData={image} sizes={sizes} />
              <ImageCropped
                fetchpriority="high"
                id="article-header-image"
                imageData={image}
                loading="eager"
                sizes={sizes}
                role="presentation"
                alt={image.altText || image.title}
                isFeatured={isFeatured}
                contain
              />
            </>
          )}
          <Media>
            {(image.credit || image.source) && (
              <CreditSource
                dangerouslySetInnerHTML={{
                  __html: formatCreditSource(image),
                }}
              />
            )}
          </Media>
        </>
      )}
      {displayDate && <DateWrapper isFeatured={isFeatured}>{displayDate}</DateWrapper>}
      {showAffiliateDisclaimer && (
        <Disclaimer isFeatured={isFeatured}>
          We carefully vet all products and services featured on mindbodygreen using our{" "}
          <DisclaimerLink
            href="/commerce-guidelines"
            title="mindbodygreen Commerce Guidlines"
            target="_blank"
            rel="noreferrer"
          >
            commerce guidelines.
          </DisclaimerLink>{" "}
          Our selections are never influenced by the commissions earned from our links.
        </Disclaimer>
      )}
    </Wrapper>
  )
}

export default Header
